/**
 * AccordionEditor
 *
 * Property editor for accordion control type
 * Configures accordion panel styling, animation, and behavior
 *
 * SRP: Responsible ONLY for configuring accordion properties
 * Control Type: 'accordion'
 *
 * Properties Configured (8):
 * 1. itemHeaderColor - Header text color
 * 2. itemHeaderBackgroundColor - Header background color
 * 3. itemBorderColor - Item border color
 * 4. animationDuration - Expand/collapse speed (ms)
 * 5. allowMultipleExpanded - Allow multiple items expanded
 * 6. itemSpacing - Space between items (px)
 * 7. borderRadius - Rounded corners (px)
 * 8. disablePadding - Remove internal padding
 */

import React, { useCallback, useMemo } from 'react';
import type { PropertyEditor, PropertyEditorProps } from '../../../registry/PropertyEditor.types';
import type { FormControl } from '@atlas-forms/types-js';
import { ColorPropertyEditor } from '../BaseEditors/ColorPropertyEditor';
import { NumberPropertyEditor } from '../BaseEditors/NumberPropertyEditor';
import { CheckboxPropertyEditor } from '../BaseEditors/CheckboxPropertyEditor';

/**
 * Accordion configuration
 */
interface AccordionConfig {
  itemHeaderColor?: string;
  itemHeaderBackgroundColor?: string;
  itemBorderColor?: string;
  animationDuration?: number;
  allowMultipleExpanded?: boolean;
  itemSpacing?: number;
  borderRadius?: number;
  disablePadding?: boolean;
}

/**
 * AccordionEditor Component
 * Renders property editors for all accordion configuration
 */
const AccordionEditorComponent: React.FC<PropertyEditorProps> = ({
  control,
  onConfigChange,
}) => {
  const config = useMemo(
    () => (control.config as AccordionConfig) || {},
    [control.config],
  );

  const handleItemHeaderColorChange = useCallback(
    (value: string) => onConfigChange('itemHeaderColor', value),
    [onConfigChange],
  );

  const handleItemHeaderBackgroundColorChange = useCallback(
    (value: string) => onConfigChange('itemHeaderBackgroundColor', value),
    [onConfigChange],
  );

  const handleItemBorderColorChange = useCallback(
    (value: string) => onConfigChange('itemBorderColor', value),
    [onConfigChange],
  );

  const handleAnimationDurationChange = useCallback(
    (value: number) => onConfigChange('animationDuration', value),
    [onConfigChange],
  );

  const handleAllowMultipleExpandedChange = useCallback(
    (value: boolean) => onConfigChange('allowMultipleExpanded', value),
    [onConfigChange],
  );

  const handleItemSpacingChange = useCallback(
    (value: number) => onConfigChange('itemSpacing', value),
    [onConfigChange],
  );

  const handleBorderRadiusChange = useCallback(
    (value: number) => onConfigChange('borderRadius', value),
    [onConfigChange],
  );

  const handleDisablePaddingChange = useCallback(
    (value: boolean) => onConfigChange('disablePadding', value),
    [onConfigChange],
  );

  return (
    <div className="af-property-editor__section accordion-editor">
      <h3 className="af-property-editor__section-title">Accordion Properties</h3>

      <ColorPropertyEditor
        label="Item Header Color"
        value={config.itemHeaderColor || '#000000'}
        onChange={handleItemHeaderColorChange}
        helpText="Text color for accordion item headers"
      />

      <ColorPropertyEditor
        label="Item Header Background Color"
        value={config.itemHeaderBackgroundColor || '#F5F5F5'}
        onChange={handleItemHeaderBackgroundColorChange}
        helpText="Background color for accordion headers"
      />

      <ColorPropertyEditor
        label="Item Border Color"
        value={config.itemBorderColor || '#E0E0E0'}
        onChange={handleItemBorderColorChange}
        helpText="Border color for accordion items"
      />

      <NumberPropertyEditor
        label="Animation Duration (ms)"
        value={config.animationDuration || 300}
        onChange={handleAnimationDurationChange}
        min={0}
        max={2000}
        step={100}
        helpText="Speed of expand/collapse animation"
      />

      <CheckboxPropertyEditor
        label="Allow Multiple Expanded"
        value={config.allowMultipleExpanded || false}
        onChange={handleAllowMultipleExpandedChange}
        helpText="Allow multiple items to be expanded simultaneously"
      />

      <NumberPropertyEditor
        label="Item Spacing (px)"
        value={config.itemSpacing || 8}
        onChange={handleItemSpacingChange}
        min={0}
        max={50}
        step={2}
        helpText="Space between accordion items"
      />

      <NumberPropertyEditor
        label="Border Radius (px)"
        value={config.borderRadius || 4}
        onChange={handleBorderRadiusChange}
        min={0}
        max={50}
        step={1}
        helpText="Corner radius for accordion items"
      />

      <CheckboxPropertyEditor
        label="Disable Padding"
        value={config.disablePadding || false}
        onChange={handleDisablePaddingChange}
        helpText="Remove default padding from accordion items"
      />
    </div>
  );
};

AccordionEditorComponent.displayName = 'AccordionEditor';

/**
 * Accordion Property Editor Registration
 */
export const AccordionEditor: PropertyEditor = {
  type: 'accordion',
  label: 'Accordion',
  description: 'Configure accordion panel styling, animation, and behavior',
  version: '1.0.0',
  render: (props) => <AccordionEditorComponent {...props} />,
};

export default AccordionEditor;
